<template>
  <div>
    <v-btn @click="dialog = true" color="primary">
      <v-icon left>mdi-file-plus</v-icon>
      Add Data Source
    </v-btn>

    <v-dialog v-model="dialog" max-width="425">
      <v-card>
        <v-card-title>Add Data Source</v-card-title>
        <v-card-text>
          <p class="text-subtitle-2">
            Provide Data Source allow agent to automatically answer customers
            questions with accurate and relevant information
          </p>

          <v-radio-group v-model="dataSourceType" row class="mb-4">
            <v-radio label="Text" value="txt"></v-radio>
            <v-radio label="File" value="file"></v-radio>
            <v-radio label="URL" value="url"></v-radio>
          </v-radio-group>

          <v-text-field
            v-model="dataSourceName"
            label="Name"
            placeholder="Give data source name or title"
          ></v-text-field>

          <v-textarea
            v-if="dataSourceType === 'txt'"
            v-model="dataSourceContent"
            label="Text"
            placeholder="Enter text here"
          ></v-textarea>

          <v-text-field
            v-if="dataSourceType === 'url'"
            v-model="dataSourceContent"
            label="URL"
            placeholder="Enter URL here"
          ></v-text-field>

          <label class="upload-label" v-if="dataSourceType === 'file'">
            <div class="upload-content">
              <div v-if="dataSourceFile">
                <v-icon>mdi-file</v-icon>
                <div class="file-name">{{ dataSourceFile.name }}</div>
              </div>
              <div v-else>
                <v-icon>mdi-upload</v-icon>
                <p class="upload-instructions">
                  <span class="bold">Click to upload</span> or drag and drop
                </p>
                <p class="file-types">
                  PDF, EXCEL, CSV or WORD file (MAX. 10MB)
                </p>
              </div>
            </div>
            <input
              ref="fileInput"
              type="file"
              accept="application/pdf, application/vnd.ms-excel, text/csv, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              class="hidden-input"
              @change="handleFileUpload"
            />
          </label>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            :loading="loading"
            :disabled="loading"
            color="primary"
            @click="save"
          >
            Save changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "CreateDataSource",
  props: {
    botId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      dialog: false,
      loading: false,
      dataSourceType: "txt",
      dataSourceName: "",
      dataSourceContent: "",
      dataSourceFile: null,
      fileLimit: 10 * 1024 * 1024,
    };
  },
  methods: {
    showError(message) {
      this.$store.dispatch("setSnackbar", {
        text: message,
        color: "error",
        status: true,
      });
    },
    showSuccess(message) {
      this.$store.dispatch("setSnackbar", {
        text: message,
        color: "success",
        status: true,
      });
    },
    async save() {
      if (!this.dataSourceName) {
        return this.showError("Data Source name is required");
      }

      if (this.dataSourceType === "txt" && !this.dataSourceContent) {
        return this.showError("Data Source content is required");
      }

      if (this.dataSourceType === "url" && !this.dataSourceContent) {
        return this.showError("Data Source URL is required");
      }

      if (this.dataSourceType === "file" && !this.dataSourceFile) {
        return this.showError("Data Source file is required");
      }

      try {
        this.loading = true;
        const formData = {
          type: this.dataSourceType,
          name: this.dataSourceName,
          value:
            this.dataSourceType === "file"
              ? await this.fileToBase64(this.dataSourceFile)
              : this.dataSourceContent,
          file_type:
            this.dataSourceType === "file" ? this.dataSourceFile.type : "",
        };
        await this.$store.dispatch("llm/knowledge/add", {
          botID: this.botId,
          data: formData,
          headers: {
'Access-Control-Allow-Origin': '*',
'Content-Type': 'application/json'
          }
        });
        this.dialog = false;
        this.showSuccess("Data Source has been created successfully");
        this.$emit("created");
      } catch (error) {
        console.error(error);
        this.showError("An error occurred while creating data source");
      } finally {
        this.loading = false;
      }
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = (error) => reject(error);
      });
    },
    handleFileUpload(event) {
      this.dataSourceFile = event.target.files[0];

      if (this.dataSourceFile.size > this.fileLimit) {
        this.dataSourceFile = null;
        this.$store.dispatch("setErrorSnackbar", {
          text: "File size should be less than 10 MB!",
        });
      }
    },
  },
};
</script>
<style scoped>
.upload-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 16rem; /* Equivalent to h-64 */
  border: 2px dashed #d1d5db; /* Equivalent to border-gray-300 */
  border-radius: 0.5rem; /* Equivalent to rounded-lg */
  cursor: pointer;
  background-color: #f9fafb; /* Background color for light mode */
  transition: background-color 0.2s ease;
}

.upload-label:hover {
  background-color: #e5e7eb; /* Background color on hover */
}

.upload-content {
  padding-top: 1.25rem; /* Equivalent to pt-5 */
  padding-bottom: 1.5rem; /* Equivalent to pb-6 */
  text-align: center;
}

.icon {
  width: 2rem; /* Equivalent to w-8 */
  height: 2rem; /* Equivalent to h-8 */
  color: #6b7280; /* Equivalent to text-gray-500 */
  margin-bottom: 1rem; /* Equivalent to mb-4 */
}

.file-name {
  font-size: 0.75rem; /* Equivalent to text-xs */
  color: #6b7280; /* Equivalent to text-gray-500 */
}

.upload-instructions {
  margin-bottom: 0.5rem; /* Equivalent to mb-2 */
  font-size: 0.875rem; /* Equivalent to text-sm */
  color: #6b7280; /* Equivalent to text-gray-500 */
}

.bold {
  font-weight: 600; /* Equivalent to font-semibold */
}

.file-types {
  font-size: 0.75rem; /* Equivalent to text-xs */
  color: #6b7280; /* Equivalent to text-gray-500 */
}

.hidden-input {
  display: none; /* Hide the file input */
}
</style>
